import React, { useState, useEffect } from 'react';
import { usePublicStats } from '../../../hooks/unified/useJobsQueries';
import { useViewport } from '../../../hooks/useViewport';

interface Statistic {
  label: string;
  value: number;
  suffix: string;
  icon: React.ReactElement;
  color: string;
}

// Minimum display values for when real data is low/empty
// These provide a professional appearance for new deployments
const MIN_DISPLAY_VALUES = {
  active_jobs: 25,
  total_companies: 12,
  total_placements: 150,
  total_candidates: 500,
  success_rate: 92,
};

export function StatisticsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const { data: stats, isLoading } = usePublicStats();
  const { prefersReducedMotion } = useViewport();

  // Use real values if they exceed minimums, otherwise show minimums
  const displayStats = {
    active_jobs: Math.max(stats?.active_jobs || 0, MIN_DISPLAY_VALUES.active_jobs),
    total_companies: Math.max(stats?.total_companies || 0, MIN_DISPLAY_VALUES.total_companies),
    total_placements: Math.max(stats?.total_placements || 0, MIN_DISPLAY_VALUES.total_placements),
    total_candidates: Math.max(stats?.total_candidates || 0, MIN_DISPLAY_VALUES.total_candidates),
    success_rate: Math.max(stats?.success_rate || 0, MIN_DISPLAY_VALUES.success_rate),
  };

  const statistics: Statistic[] = [
    {
      label: "Active Jobs",
      value: displayStats.active_jobs,
      suffix: "+",
      color: "text-blue-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
        </svg>
      )
    },
    {
      label: "Partner Companies",
      value: displayStats.total_companies,
      suffix: "+",
      color: "text-green-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      label: "Successful Placements",
      value: displayStats.total_placements,
      suffix: "+",
      color: "text-purple-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      label: "Registered Candidates",
      value: displayStats.total_candidates,
      suffix: "+",
      color: "text-orange-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('statistics-section');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  return (
    <section id="statistics-section" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <span className="inline-block px-3 sm:px-4 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            Our Impact
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Trusted by Thousands
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mobile-px">
            Real numbers that reflect our commitment to connecting talent with opportunity.
          </p>
        </div>

        {/* Statistics Grid - 2 columns on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {statistics.map((stat, index) => (
            <StatisticCard 
              key={index} 
              statistic={stat} 
              isVisible={isVisible && !isLoading}
              delay={prefersReducedMotion ? 0 : index * 150}
              prefersReducedMotion={prefersReducedMotion}
            />
          ))}
        </div>

        {/* Success Rate Highlight */}
        {displayStats.success_rate > 0 && (
          <div className="mt-10 sm:mt-12 lg:mt-16 bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg border border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  Industry-Leading Success Rate
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Our placement success rate of {displayStats.success_rate.toFixed(0)}% demonstrates our commitment 
                  to finding the perfect match between candidates and employers.
                </p>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full mr-2 sm:mr-3"></div>
                    <span className="text-xs sm:text-sm text-gray-700">Personalized matching</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full mr-2 sm:mr-3"></div>
                    <span className="text-xs sm:text-sm text-gray-700">Expert guidance</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-purple-500 rounded-full mr-2 sm:mr-3"></div>
                    <span className="text-xs sm:text-sm text-gray-700">Career support</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-orange-500 rounded-full mr-2 sm:mr-3"></div>
                    <span className="text-xs sm:text-sm text-gray-700">Fast placement</span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div className="inline-block bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-xl sm:rounded-2xl p-6 sm:p-8">
                  <div className="text-4xl sm:text-5xl font-bold mb-1 sm:mb-2">{displayStats.success_rate.toFixed(0)}%</div>
                  <div className="text-primary-100 text-base sm:text-lg">Success Rate</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

interface StatisticCardProps {
  statistic: Statistic;
  isVisible: boolean;
  delay: number;
  prefersReducedMotion: boolean;
}

function StatisticCard({ statistic, isVisible, delay, prefersReducedMotion }: StatisticCardProps) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isVisible && statistic.value > 0) {
      // Skip animation if user prefers reduced motion
      if (prefersReducedMotion) {
        setDisplayValue(statistic.value);
        return;
      }

      const timer = setTimeout(() => {
        const duration = 2000;
        const steps = 60;
        const increment = statistic.value / steps;
        let current = 0;
        
        const counter = setInterval(() => {
          current += increment;
          if (current >= statistic.value) {
            setDisplayValue(statistic.value);
            clearInterval(counter);
          } else {
            setDisplayValue(Math.floor(current));
          }
        }, duration / steps);

        return () => clearInterval(counter);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isVisible, statistic.value, delay, prefersReducedMotion]);

  const transitionClasses = prefersReducedMotion 
    ? '' 
    : 'transition-all duration-500 transform';
  
  const visibilityClasses = prefersReducedMotion
    ? ''
    : isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0';

  return (
    <div 
      className={`bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 shadow-md sm:shadow-lg hover:shadow-xl border border-gray-100 ${transitionClasses} ${visibilityClasses}`} 
      style={{ transitionDelay: prefersReducedMotion ? '0ms' : `${delay}ms` }}
    >
      <div className="text-center">
        <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-lg sm:rounded-xl bg-gray-50 mb-3 sm:mb-4 ${statistic.color}`}>
          {statistic.icon}
        </div>
        <div className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${statistic.color}`}>
          {displayValue.toLocaleString()}{statistic.suffix}
        </div>
        <div className="text-xs sm:text-sm lg:text-base text-gray-600 font-medium">{statistic.label}</div>
      </div>
    </div>
  );
}
